import CloseIcon from "@mui/icons-material/Close";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  SelectChangeEvent,
  Snackbar,
  TextField,
} from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { VendorTicketDetail } from "../../../@types/DRO/WarrantyTicketsResponse";
import { WarrantyTicketsApi } from "../../../api/WarrantyTickets/WarrantyTicketsApi";
import { MEDIA_BASE_URL } from "../../../config/apiConfig";
import { useLoading } from "../../../views/Loader/LoadingContext";
import AcceptDialog from "./dialogs/AcceptDialog";
import RejectDialog from "./dialogs/RejectDialog";
import RepairDialog from "./dialogs/RepairDialog";
import ReplaceDialog from "./dialogs/ReplaceDialog";

const ROWS_PER_PAGE = 10;

const WarrantyRequests: React.FC = () => {
  const { setLoading } = useLoading();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [page, setPage] = useState(0);
  const [tickets, setTickets] = useState<VendorTicketDetail[]>([]);
  const [hasNext, setHasNext] = useState(false);

  const [acceptRow, setAcceptRow] = useState<VendorTicketDetail | null>(null);
  const [rejectRow, setRejectRow] = useState<VendorTicketDetail | null>(null);
  const [repairRow, setRepairRow] = useState<VendorTicketDetail | null>(null);
  const [replaceRow, setReplaceRow] = useState<VendorTicketDetail | null>(null);
  const [pictureRow, setPictureRow] = useState<VendorTicketDetail | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" | "warning" }>({ open: false, message: "", severity: "success" });
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const bulkUploadRef = useRef<HTMLInputElement>(null);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const response = await WarrantyTicketsApi.api.getWarrantyTickets(
        page,
        ROWS_PER_PAGE,
        searchQuery,
        statusFilter,
        fromDate,
        toDate
      );
      setTickets(response.data.vendorTicketDetails ?? []);
      setHasNext(response.data.hasNext ?? false);
    } catch (error) {
      console.error("Error fetching warranty tickets", error);
      setTickets([]);
    }
    setLoading(false);
  }, [page, searchQuery, statusFilter, fromDate, toDate]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const handleStatusChange = (e: SelectChangeEvent) => {
    setStatusFilter(e.target.value);
    setPage(0);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setPage(0);
  };

  const handleBulkUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    WarrantyTicketsApi.api
      .bulkUploadWarrantyTickets(file)
      .then((response) => {
        setSnackbar({
          open: true,
          message:
            response.message ||
            (response.hasErrorFile
              ? "Excel response downloaded. Please review the file."
              : "File uploaded successfully"),
          severity: response.hasErrorFile ? "warning" : "success",
        });
        fetchTickets();
      })
      .catch((error: any) => {
        setSnackbar({ open: true, message: error?.response?.data?.message || "Upload failed", severity: "error" });
      })
      .finally(() => {
        setLoading(false);
      });

    e.target.value = "";
  };

  const handleDownloadTemplate = async () => {
    setLoading(true);
    try {
      const blob = await WarrantyTicketsApi.api.downloadTemplateSheet();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "warranty_template.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading template", error);
    }
    setLoading(false);
  };

  const filtered = tickets;

  return (
    <>
      <div className="flex-1 overflow-auto bg-white p-6">
        <div className="flex items-center justify-between mb-5">
          <h1 className="text-3xl font-semibold text-gray-800">
            Warranty Requests
          </h1>
          <div className="flex items-center gap-2">
            <TextField
              type="date"
              size="small"
              label="From"
              value={fromDate}
              onChange={(e) => { setFromDate(e.target.value); setPage(0); }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: new Date().toISOString().split("T")[0] }}
              sx={{ width: 150, bgcolor: "white" }}
            />
            <TextField
              type="date"
              size="small"
              label="To"
              value={toDate}
              onChange={(e) => { setToDate(e.target.value); setPage(0); }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: new Date().toISOString().split("T")[0] }}
              sx={{ width: 150, bgcolor: "white" }}
            />
          </div>
        </div>

        {/* Filter bar */}
        <div className="flex items-center gap-3 mb-5 flex-wrap">
          <TextField
            size="small"
            placeholder="Search by UID"
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon fontSize="small" className="text-gray-400" />
                </InputAdornment>
              ),
            }}
            sx={{ width: 200, bgcolor: "white" }}
          />

          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500 font-medium">Status :</span>
            <Select
              size="small"
              value={statusFilter}
              onChange={handleStatusChange}
              sx={{ minWidth: 100, bgcolor: "white", fontSize: 14 }}>
              <MenuItem value="All">All</MenuItem>
              <MenuItem value="PENDING">Pending</MenuItem>
              <MenuItem value="ACCEPTED">Accepted</MenuItem>
            </Select>
          </div>

          <div className="ml-auto flex items-center gap-2">
            <Button
              variant="outlined"
              size="small"
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={handleDownloadTemplate}
              sx={{
                textTransform: "none",
                borderColor: "#2563EB",
                color: "#2563EB",
                "&:hover": { borderColor: "#1D4ED8", bgcolor: "#EFF6FF" },
              }}>
              Download Template
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<CloudUploadOutlinedIcon />}
              onClick={() => bulkUploadRef.current?.click()}
              sx={{
                textTransform: "none",
                bgcolor: "#2563EB",
                "&:hover": { bgcolor: "#1D4ED8" },
              }}>
              Bulk Upload
            </Button>
            <input
              ref={bulkUploadRef}
              type="file"
              accept=".xlsx"
              className="hidden"
              onChange={handleBulkUpload}
            />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead>
                <tr className="border-b border-gray-100">
                  {[
                    "Hub Name",
                    "OEM Name",
                    "Part Name",
                    "UID No.",
                    "Onboarding Date",
                    "Status",
                    "Part's Picture",
                    "Actions",
                  ].map((col) => (
                    <th
                      key={col}
                      className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((row, idx) => {
                  const isPending = row.status?.toLowerCase() === "pending";
                  return (
                    <tr
                      key={idx}
                      className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{row.hubName}</td>
                      <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{row.oemName}</td>
                      <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{row.componentName ?? "-"}</td>
                      <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{row.uid}</td>
                      <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{row.onboardingDate}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span
                          className="font-semibold"
                          style={{ color: isPending ? "#FFA007" : "#00BC84" }}>
                          {row.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<VisibilityOutlinedIcon fontSize="small" />}
                          onClick={() => row.imageId && setPictureRow(row)}
                          sx={{
                            textTransform: "none",
                            fontSize: 12,
                            borderColor: "#2563EB",
                            color: "#2563EB",
                            "&:hover": { borderColor: "#1D4ED8", bgcolor: "#EFF6FF" },
                          }}>
                          View Picture
                        </Button>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {isPending ? (
                          <div className="flex items-center gap-2">
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => setRejectRow(row)}
                              sx={{ textTransform: "none", fontSize: 12, bgcolor: "#EF4444", "&:hover": { bgcolor: "#DC2626" } }}>
                              Reject
                            </Button>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => setAcceptRow(row)}
                              sx={{ textTransform: "none", fontSize: 12, bgcolor: "#22C55E", "&:hover": { bgcolor: "#16A34A" } }}>
                              Accept
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => setRepairRow(row)}
                              sx={{ textTransform: "none", fontSize: 12, bgcolor: "#22C55E", "&:hover": { bgcolor: "#16A34A" } }}>
                              Repair
                            </Button>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => setReplaceRow(row)}
                              sx={{ textTransform: "none", fontSize: 12, bgcolor: "#0D9488", "&:hover": { bgcolor: "#0F766E" } }}>
                              Replace
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-10 text-center text-gray-400">
                      No records found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {(page > 0 || hasNext) && (
            <div className="flex items-center justify-end gap-1 px-4 py-3 border-t border-gray-100">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="w-7 h-7 flex items-center justify-center rounded text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed text-sm">
                ‹
              </button>
              <span className="text-sm text-gray-600 px-2">Page {page + 1}</span>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={!hasNext}
                className="w-7 h-7 flex items-center justify-center rounded text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed text-sm">
                ›
              </button>
            </div>
          )}
        </div>
      </div>

      <AcceptDialog
        open={!!acceptRow}
        row={acceptRow}
        onClose={() => setAcceptRow(null)}
        onSuccess={fetchTickets}
      />
      <RejectDialog
        open={!!rejectRow}
        row={rejectRow}
        onClose={() => setRejectRow(null)}
        onSuccess={fetchTickets}
      />
      <RepairDialog
        open={!!repairRow}
        row={repairRow}
        onClose={() => setRepairRow(null)}
        onSuccess={fetchTickets}
      />
      <ReplaceDialog
        open={!!replaceRow}
        row={replaceRow}
        onClose={() => setReplaceRow(null)}
        onSuccess={fetchTickets}
      />

      {/* Image Preview Modal */}
      <Dialog
        open={!!pictureRow}
        onClose={() => setPictureRow(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle
          sx={{
            fontWeight: 600,
            fontSize: 16,
            color: "#1F2937",
            pb: 1,
          }}>
          {/* Image Name */}
          <IconButton
            onClick={() => setPictureRow(null)}
            sx={{ position: "absolute", right: 0, top: -2, color: "#6B7280" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pb: 3, px: 3 }}>
          <div
            className="flex items-center justify-center rounded-lg"
            style={{
              border: "1.5px dashed #D1D5DB",
              minHeight: 220,
              background: "#FAFAFA",
            }}>
            {pictureRow?.imageId ? (
              <img
                src={`${MEDIA_BASE_URL}/${pictureRow.imageId}`}
                alt="Part"
                style={{ maxHeight: 280, maxWidth: "100%", objectFit: "contain", borderRadius: 8 }}
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = "none";
                  (e.currentTarget.nextSibling as HTMLElement).style.display = "flex";
                }}
              />
            ) : null}
            <div
              className="flex flex-col items-center justify-center text-gray-300 gap-2"
              style={{ display: pictureRow?.imageId ? "none" : "flex" }}>
              <ImageOutlinedIcon sx={{ fontSize: 48, color: "#D1D5DB" }} />
              <span className="text-sm text-gray-400">Image Preview</span>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default WarrantyRequests;
